/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Checkbox from '../Checkbox/Checkbox';
import Datebox from '../Datebox/Datebox';
import Combobox from '../Combobox/Combobox';
import Button from '../Button/Button';
import Label from '../Label/Label';
import Section from '../Section/Section';
import MoreIcon from './baseline_expand_more_black_24dp.png';
import LessIcon from './baseline_expand_less_black_24dp.png';
import './Search.css';

/**
 * Represents the search component.
 */

export default class Search extends Component {

    /**
     * Properties.
     */

    static propTypes = {
        context:                PropTypes.object.isRequired,
        entitytypes:            PropTypes.array,
        users:                  PropTypes.array,
        onClose:                PropTypes.func.isRequired,
        onSearch:               PropTypes.func.isRequired
    }

    /**
     * Default state.
     */

    state = {
        criteria:               {},
        entityypestate:         {}
    }

    /**
     * Toggles the specified checkbox.
     * 
     * @param   {Object}    checkbox    checkbox
     */

    _toggleCheckbox = (checkbox) => {

        // Toggling

        this[checkbox].toggle();
    }

    /**
     * Stores the specified user in the criteria.
     * 
     * @param   {Object}    user    user that has to store
     */

    _userSelected = (user) => {

        // Getting the criteria

        let criteria = this.state.criteria;

        // Storing the user in the criteria

        criteria.user = user;
        this.setState({ criteria: criteria });
    }

    /**
     * Toggles the attribute visibility of the specified entity type.
     * 
     * @param   {String}    id      ID of the entity type
     */

    _toggleEntitytype = (id) => {
        let entityypestate = this.state.entityypestate;
        let show = entityypestate['entitytype' + id];

        if (show) {
            delete entityypestate['entitytype' + id];
        } else {
            entityypestate['entitytype' + id] = true;
        }

        this.setState({ entityypestate: entityypestate });
    }

    /**
     * Creates the search criteria.
     */

    _search = () => {

        // Preparing

        let criteria = {
            advanced: true
        };

        // Setting the date

        if (!this.date.isEmpty()) {
            criteria.date = this.date.getValue();
        }

        // Setting the user

        if (this.state.criteria.user) {
            criteria.user = this.state.criteria.user;
        }

        // Setting the rules

        criteria.pattern = this.pattern.getValue();
        criteria.caseinsesitive = this.caseinsesitive.getValue();
        criteria.tags = this.tags.getValue();
        criteria.versions = this.versions.getValue();
        criteria.filter = this.filter.getValue();

        // Setting the entity types

        criteria.entitytypes = {};

        this.props.entitytypes.forEach(item => {
            if (this['entitytype' + item.id].getValue()) {
                criteria.entitytypes[item.id] = JSON.parse(JSON.stringify(item));
                criteria.entitytypes[item.id].selectedattr = {};

                // Setting the attributes

                item.attributes.forEach(attr => {
                    if (attr.type !== 'comment' && this['attribute' + attr.id].getValue()) {
                        criteria.entitytypes[item.id].selectedattr[attr.id] = true;
                    }
                });
            }
        });

        // Storing the criteria

        this.setState({ criteria: criteria }, () => {

            // Searching

            this.props.onSearch(criteria);

            // Closing the search window

            this.props.onClose();    
        });
    }

    /** 
     * Search renderer.
     */

    render() {
        return (
            <div className="search vertical" style={{ width: (this.props.context.environment.screen.width - 66) + 'px', height: (this.props.context.environment.screen.usefullheight - 102) + 'px' }}>

                {/* Search container */}

                <div className="search-scroll-container" style={{ height: (this.props.context.environment.screen.usefullheight - 150) + 'px' }}>

                    {/* Section for the date and user */}

                    <Section label={this.props.context.locale.search.section.dateanduser} topgap={8} />

                    {/* Modification date */}

                    <Datebox ref={c => {this.date = c;}} label={this.props.context.locale.search.field.date.label} labelwidth={160} tooltip={this.props.context.locale.search.field.date.tooltip} />
                    
                    {/* User */}

                    <Combobox ref={c => {this.user = c;}} label={this.props.context.locale.search.field.user.label} labelwidth={160} labelposition="left" tooltip={this.props.context.locale.search.field.user.tooltip} width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 256) : 200} source={this.props.users} idattr="uid" nameattr="name" onSelect={this._userSelected} />

                    {/* Section rules */}

                    <Section label={this.props.context.locale.search.section.rules} />

                    {/* Pattern */}

                    <div className="horizontal" style={{marginBottom: '4px' }}>
                        <Checkbox ref={c => {this.pattern = c;}} tooltip={this.props.context.locale.search.field.pattern.tooltip} checked />
                        <Label text={this.props.context.locale.search.field.pattern.label} tooltip={this.props.context.locale.search.field.pattern.tooltip} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'pattern')} />
                    </div>

                    {/* Case insesitive */}

                    <div className="horizontal" style={{marginBottom: '4px' }}>
                        <Checkbox ref={c => {this.caseinsesitive = c;}} tooltip={this.props.context.locale.search.field.caseinsesitive.tooltip} checked />
                        <Label text={this.props.context.locale.search.field.caseinsesitive.label} tooltip={this.props.context.locale.search.field.caseinsesitive.tooltip} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'caseinsesitive')} />
                    </div>

                    {/* Tags */}

                    <div className="horizontal" style={{marginBottom: '4px' }}>
                        <Checkbox ref={c => {this.tags = c;}} tooltip={this.props.context.locale.search.field.tags.tooltip} />
                        <Label text={this.props.context.locale.search.field.tags.label} tooltip={this.props.context.locale.search.field.tags.tooltip} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'tags')} />
                    </div>

                    {/* Versions */}

                    <div className="horizontal" style={{marginBottom: '4px' }}>
                        <Checkbox ref={c => {this.versions = c;}} tooltip={this.props.context.locale.search.field.versions.tooltip} />
                        <Label text={this.props.context.locale.search.field.versions.label} tooltip={this.props.context.locale.search.field.versions.tooltip} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'versions')}/>
                    </div>

                    {/* Filter */}

                    <div className="horizontal" style={{marginBottom: '16px' }}>
                        <Checkbox ref={c => {this.filter = c;}} tooltip={this.props.context.locale.search.field.filter.tooltip} />
                        <Label text={this.props.context.locale.search.field.filter.label} tooltip={this.props.context.locale.search.field.filter.tooltip} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'filter')} />
                    </div>

                    {/* Section entity types */}

                    <Section label={this.props.context.locale.search.section.entitytypes} topgap={8} />

                    { this.props.entitytypes.map((item, index) =>
                        <div key={index}>
                            <div className="horizontal" style={{marginBottom: '4px' }}>
                                <Checkbox ref={c => {this['entitytype' + item.id] = c;}} checked tooltip={item.name} />
                                <Label text={item.name} tooltip={item.name} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'entitytype' + item.id)} />
                                <img ref={c => {this['entitytypeicon' + item.id] = c;}} src={this.state.entityypestate['entitytype' + item.id] ? LessIcon : MoreIcon} alt="Show attributes" className="search-entitytype-icon" title={this.props.context.locale.search.entitytypes.more} onClick={this._toggleEntitytype.bind(this, item.id)}></img>
                            </div>
                            <div id={'attrcontainer' + item.id} style={{ display: this.state.entityypestate['entitytype' + item.id] ? undefined : 'none' }}>
                                { item.attributes.map((attr, index) =>
                                    attr.type !== 'comment' && <div key={index} className="vertical search-attributes">
                                        <div className="horizontal" style={{marginBottom: '4px' }}>
                                            <Checkbox ref={c => {this['attribute' + attr.id] = c;}} checked tooltip={attr.name} />
                                            <Label text={attr.name} tooltip={attr.name} leftgap={8} onTouch={this._toggleCheckbox.bind(this, 'attribute' + attr.id)} />
                                        </div>
                                    </div>
                                )}
                            </div>
                    </div>
                    )}
                </div>

                {/* Buttons */}

                <div className="search-button-container horizontal">

                    {/* Spacer */}

                    <div className="flex"></div>

                    {/* Cancel */}

                    <Button label={this.props.context.locale.search.buttons.cancel.label} tooltip={this.props.context.locale.search.buttons.cancel.tooltip} width={60} onTouch={this.props.onClose} />

                    {/* Ok */}

                    <Button label={this.props.context.locale.search.buttons.ok.label} tooltip={this.props.context.locale.search.buttons.ok.tooltip} leftgap={16} width={60} onTouch={this._search} />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Search/Search.js